import { api } from "@/api";
import {
  ImageModelInfo,
  ImageModelViewInfo,
  ModelViewInfo,
  VideoModelInfo,
  VideoModelViewInfo,
} from "@/types/mediaArtifact";
import {
  convertImageModelInfoList,
  convertVideoModelInfoList,
} from "@/util/modelUtils";
import { threadStore } from "./thread";
import { action, computed, makeAutoObservable, observable } from "mobx";

type SetKeys = keyof Pick<
  ModelStore,
  "imageModels" | "videoModels" | "loading" | "cacheThreadId"
>;

/**
 * 模型配置 Store
 * 管理图片和视频生成模型的配置信息
 */
export class ModelStore {
  @observable imageModels: ImageModelInfo[] = [];
  @observable videoModels: VideoModelInfo[] = [];
  @observable loading: boolean = false;
  @observable cacheThreadId: string | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  @computed get imageModelViewList(): ImageModelViewInfo[] {
    return convertImageModelInfoList(this.imageModels);
  }

  @computed get videoModelViewList(): VideoModelViewInfo[] {
    return convertVideoModelInfoList(
      this.videoModels,
      threadStore.threadOptions.resolution
    );
  }

  @action set<K extends SetKeys>(k: K, v: ModelStore[K]) {
    this[k] = v as never;
  }

  /**
   * 根据 modelValue 获取 modelViewInfo
   */
  getModelViewInfo(modelValue: string): ModelViewInfo | undefined {
    return (
      this.imageModelViewList.find((info) =>
        info.modelIds.includes(modelValue)
      ) ||
      this.videoModelViewList.find((info) => info.modelIds.includes(modelValue))
    );
  }

  /**
   * 获取模型配置选项
   */
  @action async fetchModelOptions(threadId: string) {
    if (this.loading) {
      return;
    }

    // Only use cached data if it's for the same thread and both arrays have data
    if (
      this.cacheThreadId === threadId &&
      this.imageModels.length > 0 &&
      this.videoModels.length > 0
    ) {
      return;
    }

    this.loading = true;

    try {
      const res = await api.threads.listTools(threadId);

      const imageModels: ImageModelInfo[] =
        (res.data.generate_image_for_shot_by_index.options
          ?.models as ImageModelInfo[]) || [];

      const videoModels: VideoModelInfo[] =
        (res.data.generate_video_for_shot_by_index.options
          ?.models as VideoModelInfo[]) || [];

      // Only set cacheThreadId if we actually got model items (non-empty arrays)
      const hasValidCache = imageModels.length > 0 && videoModels.length > 0;

      this.set("imageModels", imageModels);
      this.set("videoModels", videoModels);
      this.set("cacheThreadId", hasValidCache ? threadId : null);
    } finally {
      this.loading = false;
    }
  }
}

export const modelStore = new ModelStore();
